import { useCallback, useState, useRef, useEffect } from 'react';
import { FilterConfig, FieldOption } from '@aidkitorg/types/lib/admin-panel/filters';

type UseLocalFilterPanelOptions = {
  availableFields: FieldOption[];
  enableFlushOnMount?: boolean;
  initialFilterConfig?: FilterConfig;
};

/**
 * Hook that provides pre-configured props for FilterPanel component
 * without requiring a context provider. Useful for simple, self-contained filter panels.
 */
export const useLocalFilterPanel = (
  instanceId: string,
  options: UseLocalFilterPanelOptions
) => {
  const {
    availableFields,
    enableFlushOnMount = false,
    initialFilterConfig = { enabled: false, config: { logic: 'and', conditions: [] } }
  } = options;

  const [filterConfig, setFilterConfig] = useState<FilterConfig>(initialFilterConfig);
  const filterRef = useRef<any>(null);

  const handleFilterChange = useCallback((newConfig: FilterConfig) => {
    setFilterConfig(newConfig);
  }, []);

  // On mount, flush the FilterPanel to sync persisted sessionStorage state back to parent
  useEffect(() => {
    if (enableFlushOnMount && filterRef.current?.flush) {
      filterRef.current.flush();
    }
  }, [enableFlushOnMount]);

  return {
    // FilterPanel props
    filterPanelProps: {
      value: filterConfig,
      onChange: handleFilterChange,
      availableFields,
      instanceId,
      ref: filterRef
    },
    // Direct access to values
    filterConfig,
    setFilterConfig,
    availableFields,
    filterRef
  };
};
